import React, { FC } from 'react';
import { connect } from 'react-redux';

import { ReduxState } from 'app/redux/types';

import { Button, ButtonIntent, ButtonType } from 'app/design-system/components/Button';
import { useTranslation } from 'app/lang';
import { StakeType } from 'app/lib/staking/constants';

import { ActionButtonState, StakeErrorCode, SubmissionState } from '../../constants';
import { RecordOfBetInfoType } from '../../records';
import { selectBets } from '../../selectors/entries';
import { selectCurrentStakeType } from '../../selectors/stakeTypes';
import { selectSingleBetsInfo } from '../../selectors/stakeTypes/single';
import { selectSubmission } from '../../selectors/submission';
import actionButtonState from '../../utils/action-button-state';

import dictionary from './ActionButton.dictionary.json';

import styles from './ActionButton.module.scss';

// ---

type StoreProps = {
  state: ActionButtonState;
  betsSize: number;
  hasServerError: boolean;
  isSingle: boolean;
};

const MINIMUM_BET_SIZE = 1;

const ActionButton: FC<StoreProps> = ({
  state,
  betsSize,
  hasServerError,
  isSingle,
}) => {
  const { t } = useTranslation();

  const sizeBetForPluralForm =  isSingle ? betsSize : MINIMUM_BET_SIZE;

  const getLabelByState = () => {
    if (hasServerError) {
      return t(dictionary.labelByStateError);
    }

    switch (state) {
      case ActionButtonState.ACCESS_IS_RESTRICTED:
        return t(dictionary.accessIsRestricted);

      case ActionButtonState.DISABLED:
      case ActionButtonState.READY:
        return t(dictionary.makeBet, { count: sizeBetForPluralForm });

      case ActionButtonState.UNAUTHORIZED:
        return t(dictionary.unauthorized, { count: sizeBetForPluralForm });

      case ActionButtonState.UNKNOWN:
      default:
        return t(dictionary.labelByStateUnknown);
    }
  };

  const disabled = (
    state !== ActionButtonState.READY
    && state !== ActionButtonState.UNAUTHORIZED
  );

  return (
    <div data-test="actionButton-container">
      {hasServerError && (
        <p className={styles.error}
           data-test="actionButton-error">
          {t(dictionary.betWasNotMade, { count: betsSize })}
        </p>
      )}

      <Button type={ButtonType.SUBMIT}
              intent={ButtonIntent.CALL_TO_ACTION}
              disabled={disabled}
              data-test="actionButton-container"
              className={styles.button}>
        {getLabelByState()}
      </Button>
    </div>
  );
};


const mapStateToProps = (state: ReduxState): StoreProps => {
  const betInfo: RecordOfBetInfoType = selectSingleBetsInfo(state).first();
  const error = betInfo?.get('error');
  const stakeType = selectCurrentStakeType(state);

  const disabled = selectSubmission(state) !== SubmissionState.AWAITING;
  const betsSize = selectBets(state).size;

  return {
    betsSize,
    isSingle: stakeType.has(StakeType.SINGLE),
    state: actionButtonState(state),
    hasServerError: error?.code === StakeErrorCode.INTERNAL_SYSTEM_ERROR && !disabled,
  };
};

export default connect(mapStateToProps)(ActionButton);
